import type { CameraView } from '@/modules/game-domain/render-snapshot';

/** Each overview chunk costs 256² RGBA pixels, independent of the camera's world coverage. */
export const OVERVIEW_CHUNK_PIXELS = 256;
export const OVERVIEW_CACHE_CHUNKS = 96;
export type GroundChunk = { key: string; x: number; y: number; size: number; stride: number; resolution: number };

/** At least 48 screen pixels across each coarse diamond; the close view keeps exact cells. */
export function groundStride(zoom: number): number {
  if (!Number.isFinite(zoom) || zoom >= .5 || zoom <= 0) return 1;
  return 2 ** Math.ceil(Math.log2(.5 / zoom));
}

/** Floor is essential west/north of the world origin: neighboring chunks share exact boundaries. */
export function overviewChunks(camera: CameraView, width: number, height: number): GroundChunk[] {
  const zoom = camera.zoom ?? 1;
  const stride = groundStride(zoom);
  const size = 512 * stride;
  // drawGround quantizes its rebuild key by up to 24 world pixels. Keep a conservative
  // overscan so crossing a chunk boundary during a smaller pan never exposes an empty strip.
  const halfW = width / (2 * zoom) + 96, halfH = height / (2 * zoom) + 96;
  const result: GroundChunk[] = [];
  for (let x = Math.floor((camera.finalX - halfW) / size); x <= Math.floor((camera.finalX + halfW) / size); x++) {
    for (let y = Math.floor((camera.finalY - halfH) / size); y <= Math.floor((camera.finalY + halfH) / size); y++) {
      result.push({ key: `${stride}:${x},${y}`, x: x * size, y: y * size, size, stride, resolution: .5 / stride });
    }
  }
  return result;
}

/** LRU owns generated resources; trimming happens after a frame so all visible entries stay live. */
export class GroundChunkCache<T> {
  private entries = new Map<string, T>();
  constructor(private readonly dispose: (entry: T) => void, private readonly capacity = OVERVIEW_CACHE_CHUNKS) {}
  get size(): number { return this.entries.size; }
  getOrCreate(key: string, create: () => T): T {
    if (this.entries.has(key)) {
      const entry = this.entries.get(key)!;
      this.entries.delete(key); this.entries.set(key, entry);
      return entry;
    }
    const entry = create(); this.entries.set(key, entry); return entry;
  }
  values(): IterableIterator<T> { return this.entries.values(); }
  invalidate(shouldInvalidate: (entry: T, key: string) => boolean): void {
    for (const [key, entry] of this.entries) {
      if (!shouldInvalidate(entry, key)) continue;
      this.entries.delete(key); this.dispose(entry);
    }
  }
  trim(visible: ReadonlySet<string>): void {
    for (const [key, entry] of this.entries) {
      if (this.entries.size <= Math.max(this.capacity, visible.size)) break;
      if (visible.has(key)) continue;
      this.entries.delete(key); this.dispose(entry);
    }
  }
  clear(): void {
    for (const entry of this.entries.values()) this.dispose(entry);
    this.entries.clear();
  }
}
